/**
 * S88 — Envío masivo de email en 4 pasos (LeadCEM-style).
 * Plantilla → A quién → Asunto → Cupo y confirmar.
 * Cero menús técnicos. El monstruo del backend queda detrás.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ChevronLeft, ChevronRight, Check, Mail, Users, FileText,
  Send, Loader2, Upload, AlertTriangle, Calendar,
} from 'lucide-react';

const STEPS = [
  { key: 'tpl', label: 'Plantilla', icon: FileText },
  { key: 'who', label: 'A quién', icon: Users },
  { key: 'msg', label: 'Asunto', icon: Mail },
  { key: 'go', label: 'Confirmar', icon: Send },
];

const MERGE_TAGS = [
  { key: 'nombre_cliente', label: 'Nombre' },
  { key: 'tenant_name', label: 'Tu negocio' },
  { key: 'enlace', label: 'Enlace' },
  { key: 'fecha_cita', label: 'Fecha cita' },
];

const SOURCES = [
  { id: 'crm_all', label: 'Todos mis leads con email', hint: 'CRM activo' },
  { id: 'crm_stage', label: 'Leads de una etapa del embudo', hint: 'Pipeline' },
  { id: 'crm_dates', label: 'Leads creados entre fechas', hint: 'Rango' },
  { id: 'list', label: 'Una lista que ya tengo', hint: 'Audiencia' },
  { id: 'csv', label: 'Subir base CSV', hint: 'nombre, email' },
];

export default function EmailMassSendWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [source, setSource] = useState('crm_all');
  const [audienceId, setAudienceId] = useState('');
  const [stageKey, setStageKey] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  const [csvNote, setCsvNote] = useState('');
  const [subject, setSubject] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [result, setResult] = useState(null);

  const selectedTpl = templates.find(t => t.id === templateId);

  const load = useCallback(async () => {
    try {
      const [tRes, mRes] = await Promise.all([
        api.get('/tenant/email/templates'),
        api.get('/tenant/email/mass/sources-meta').catch(() => ({ data: null })),
      ]);
      const tpls = (tRes.data || []).filter(t => t.status !== 'archived');
      // prefer active + visual first
      tpls.sort((a, b) => {
        const score = (t) => (t.status === 'active' ? 2 : 0) + (t.design_json ? 1 : 0);
        return score(b) - score(a);
      });
      setTemplates(tpls);
      setMeta(mRes.data);
      if (mRes.data?.etapas?.[0]?.key) setStageKey(mRes.data.etapas[0].key);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedTpl && !subject) setSubject(selectedTpl.subject || '');
    if (selectedTpl && !campaignName) setCampaignName(selectedTpl.name || '');
  }, [selectedTpl]); // eslint-disable-line

  const parseCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || '');
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) {
        setCsvNote('Archivo vacío o solo encabezados');
        setCsvRows([]);
        return;
      }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const iEmail = headers.findIndex(h => ['email', 'correo', 'mail'].includes(h));
      const iName = headers.findIndex(h => ['nombre', 'name', 'first_name'].includes(h));
      if (iEmail < 0) {
        setCsvNote('Falta columna email o correo');
        setCsvRows([]);
        return;
      }
      const rows = [];
      for (let i = 1; i < lines.length && rows.length < 2000; i++) {
        const vals = lines[i].split(',').map(v => v.trim());
        const email = vals[iEmail] || '';
        if (!email.includes('@')) continue;
        rows.push({ email, nombre: iName >= 0 ? vals[iName] : email.split('@')[0] });
      }
      setCsvRows(rows);
      setCsvNote(`${rows.length} contactos listos`);
    };
    reader.readAsText(file);
  };

  const audiencePayload = () => ({
    source,
    audience_id: source === 'list' ? audienceId : undefined,
    stage_key: source === 'crm_stage' ? stageKey : undefined,
    date_from: source === 'crm_dates' ? dateFrom : undefined,
    date_to: source === 'crm_dates' ? dateTo : undefined,
    csv_rows: source === 'csv' ? csvRows : undefined,
  });

  const runEstimate = async () => {
    setLoading(true);
    setError('');
    setEstimate(null);
    try {
      const { data } = await api.post('/tenant/email/mass/estimate', audiencePayload());
      setEstimate(data);
      setStep(3);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'No se pudo calcular el envío';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const launch = async () => {
    if (!templateId) { setError('Elige una plantilla'); return; }
    if (!estimate || (estimate.pueden_enviarse || 0) < 1 && sendNow) {
      setError('No hay destinatarios que se puedan enviar con tu cupo');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/tenant/email/mass/launch', {
        template_id: templateId,
        subject_override: subject,
        campaign_name: campaignName,
        send_now: sendNow,
        scheduled_at: sendNow ? undefined : (scheduledAt ? new Date(scheduledAt).toISOString() : undefined),
        confirm: true,
        ...audiencePayload(),
      });
      setResult(data);
      toast.success(data.message || 'Listo');
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'Falló el envío';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const insertTag = (key) => {
    setSubject(s => `${s}{{${key}}}`);
  };

  const canNext = () => {
    if (step === 0) return !!templateId;
    if (step === 1) {
      if (source === 'list') return !!audienceId;
      if (source === 'crm_stage') return !!stageKey;
      if (source === 'crm_dates') return !!dateFrom && !!dateTo;
      if (source === 'csv') return csvRows.length > 0;
      return true;
    }
    if (step === 2) return !!subject.trim();
    return true;
  };

  const goNext = async () => {
    setError('');
    if (step === 2) {
      await runEstimate();
      return;
    }
    if (step < 3) setStep(s => s + 1);
  };

  if (result) {
    return (
      <div className="max-w-lg mx-auto space-y-4 py-8" data-testid="mass-result">
        <div className={`rounded-2xl border p-6 text-center ${result.ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
          <div className="w-12 h-12 rounded-full bg-white mx-auto flex items-center justify-center mb-3">
            <Check className="w-6 h-6 text-emerald-600" />
          </div>
          <h2 className="text-lg font-bold text-[#0F172A]">
            {result.mode === 'scheduled' ? 'Envío programado' : 'Envío listo'}
          </h2>
          <p className="text-sm text-[#64748B] mt-2">{result.message}</p>
          <div className="grid grid-cols-3 gap-2 mt-4 text-center">
            <div className="bg-white rounded-xl p-2">
              <p className="text-xl font-bold text-emerald-600">{result.enviados ?? 0}</p>
              <p className="text-[10px] text-[#94A3B8]">Enviados</p>
            </div>
            <div className="bg-white rounded-xl p-2">
              <p className="text-xl font-bold text-red-500">{result.fallidos ?? 0}</p>
              <p className="text-[10px] text-[#94A3B8]">Fallidos</p>
            </div>
            <div className="bg-white rounded-xl p-2">
              <p className="text-xl font-bold text-amber-600">{result.bloqueados_baja ?? 0}</p>
              <p className="text-[10px] text-[#94A3B8]">Bajas</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={() => navigate('/tenant/email/envios')}>
            Ver envíos
          </Button>
          <Button className="bg-[#0F172A] text-white" onClick={() => {
            setResult(null); setStep(0); setEstimate(null); setTemplateId(''); setSubject('');
          }}>
            Otro envío
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="email-mass-wizard">
      <div>
        <button type="button" onClick={() => navigate('/tenant/email/envios')}
          className="text-xs text-[#64748B] hover:text-[#0F172A] flex items-center gap-1 mb-2">
          <ChevronLeft className="w-3.5 h-3.5" /> Volver a Envíos
        </button>
        <h2 className="text-lg font-bold text-[#0F172A]">Nuevo envío masivo</h2>
        <p className="text-sm text-[#64748B]">
          4 pasos. Eliges el diseño, a quién, el asunto, y confirmas con tu cupo del plan.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="mass-stepper">
        {STEPS.map((s, i) => (
          <div key={s.key}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
              i === step ? 'bg-[#F72585] text-white' : i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
            }`}>
            {i < step ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-xl flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* 1 Plantilla */}
      {step === 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-4" data-testid="mass-step-tpl">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-[#0F172A]">1. Elige el diseño del correo</h3>
            <button type="button" className="text-[11px] text-[#F72585] underline"
              onClick={() => navigate('/tenant/email/plantillas')}>
              Crear o editar diseños
            </button>
          </div>
          {templates.length === 0 ? (
            <div className="text-center py-8 border border-dashed rounded-xl">
              <p className="text-sm text-[#64748B]">Aún no tienes plantillas de email.</p>
              <Button className="mt-3 bg-[#0F172A] text-white" onClick={() => navigate('/tenant/email/plantillas')}>
                Ir a diseños
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {templates.map(t => (
                <button key={t.id} type="button" onClick={() => setTemplateId(t.id)}
                  className={`w-full text-left border rounded-xl p-3 transition-colors ${
                    templateId === t.id ? 'border-[#F72585] bg-[#FFF5F9] ring-1 ring-[#F72585]/30' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                  }`}
                  data-testid={`mass-tpl-${t.id}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{t.name}</p>
                  <p className="text-[11px] text-[#94A3B8] truncate mt-0.5">{t.subject}</p>
                  <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#64748B]">
                    {t.status === 'active' ? 'Lista' : 'Borrador'}{t.design_json ? ' · Visual' : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2 A quién */}
      {step === 1 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-4" data-testid="mass-step-who">
          <h3 className="text-sm font-bold text-[#0F172A]">2. ¿A quién se lo mandas?</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {SOURCES.map(s => (
              <button key={s.id} type="button" onClick={() => setSource(s.id)}
                className={`text-left border rounded-xl p-3 ${
                  source === s.id ? 'border-[#F72585] bg-[#FFF5F9]' : 'border-[#E2E8F0]'
                }`}
                data-testid={`mass-source-${s.id}`}>
                <p className="text-xs font-semibold text-[#0F172A]">{s.label}</p>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">{s.hint}
                  {s.id === 'crm_all' && meta ? ` · ${meta.leads_con_email} leads` : ''}
                </p>
              </button>
            ))}
          </div>

          {source === 'list' && (
            <select value={audienceId} onChange={e => setAudienceId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="mass-list-select">
              <option value="">Elige una lista…</option>
              {(meta?.listas || []).map(a => (
                <option key={a.id} value={a.id}>{a.name} ({a.count})</option>
              ))}
            </select>
          )}
          {source === 'crm_stage' && (
            <select value={stageKey} onChange={e => setStageKey(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="mass-stage-select">
              {(meta?.etapas || []).map(s => (
                <option key={s.key} value={s.key}>{s.name}</option>
              ))}
            </select>
          )}
          {source === 'crm_dates' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-[#64748B]">Desde</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="mass-date-from" />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B]">Hasta</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="mass-date-to" />
              </div>
            </div>
          )}
          {source === 'csv' && (
            <div className="space-y-2">
              <p className="text-xs text-[#64748B]">CSV con columnas <strong>nombre, email</strong> (o correo).</p>
              <label className="flex items-center gap-2 border border-dashed rounded-xl p-4 cursor-pointer hover:bg-[#F8FAFC]">
                <Upload className="w-4 h-4 text-[#64748B]" />
                <span className="text-xs text-[#0F172A]">Elegir archivo CSV</span>
                <input type="file" accept=".csv,text/csv" className="hidden" data-testid="mass-csv"
                  onChange={e => e.target.files?.[0] && parseCsv(e.target.files[0])} />
              </label>
              {csvNote && <p className="text-xs text-emerald-700">{csvNote}</p>}
            </div>
          )}
        </div>
      )}

      {/* 3 Asunto */}
      {step === 2 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-4" data-testid="mass-step-msg">
          <h3 className="text-sm font-bold text-[#0F172A]">3. Asunto y nombre del envío</h3>
          <div>
            <label className="text-[10px] text-[#64748B]">Nombre interno (solo tú lo ves)</label>
            <Input value={campaignName} onChange={e => setCampaignName(e.target.value)}
              placeholder="Promo marzo" data-testid="mass-name" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B]">Asunto del correo</label>
            <Input value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="{{tenant_name}} tiene algo para ti" data-testid="mass-subject" />
            <div className="flex flex-wrap gap-1 mt-2">
              {MERGE_TAGS.map(t => (
                <button key={t.key} type="button" onClick={() => insertTag(t.key)}
                  className="text-[10px] px-2 py-1 rounded-full border border-[#E2E8F0] text-[#64748B] hover:border-[#F72585] hover:text-[#F72585]">
                  + {t.label}
                </button>
              ))}
            </div>
          </div>
          {selectedTpl && (
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-xs text-[#64748B]">
              Diseño: <strong className="text-[#0F172A]">{selectedTpl.name}</strong>
              <p className="mt-1">El cuerpo del correo sale de la plantilla (con tu logo). Aquí solo ajustas el asunto.</p>
            </div>
          )}
        </div>
      )}

      {/* 4 Confirmar */}
      {step === 3 && estimate && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-4" data-testid="mass-step-go">
          <h3 className="text-sm font-bold text-[#0F172A]">4. Revisa cupo y confirma</h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-center">
              <p className="text-2xl font-bold text-[#0F172A]">{estimate.elegibles}</p>
              <p className="text-[10px] text-[#94A3B8]">Con email</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{estimate.pueden_enviarse}</p>
              <p className="text-[10px] text-emerald-700">Se van a enviar</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{estimate.no_se_enviaran}</p>
              <p className="text-[10px] text-amber-700">No se envían</p>
            </div>
            <div className="rounded-xl bg-sky-50 border border-sky-100 p-3 text-center">
              <p className="text-2xl font-bold text-sky-700">{estimate.cupo?.restantes ?? '—'}</p>
              <p className="text-[10px] text-sky-700">Cupo del mes</p>
            </div>
          </div>

          {!estimate.cupo?.ok && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
              {estimate.cupo?.motivo || 'Email no listo. Configura tu dominio en Email → Mi dominio.'}
            </div>
          )}

          {estimate.bloqueados_baja > 0 && (
            <p className="text-xs text-[#64748B]">
              {estimate.bloqueados_baja} en lista de bajas (no se tocan — protege tu reputación).
            </p>
          )}

          {estimate.preview?.length > 0 && (
            <div className="text-xs text-[#64748B]">
              Ejemplos: {estimate.preview.map(p => p.name || p.email).join(', ')}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setSendNow(true)}
              className={`px-3 py-1.5 text-xs rounded-full border ${sendNow ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0]'}`}>
              Enviar ahora
            </button>
            <button type="button" onClick={() => setSendNow(false)}
              className={`px-3 py-1.5 text-xs rounded-full border ${!sendNow ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0]'}`}>
              <Calendar className="w-3 h-3 inline mr-1" /> Programar
            </button>
          </div>
          {!sendNow && (
            <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
              data-testid="mass-schedule" />
          )}

          <Button
            onClick={launch}
            disabled={loading || (sendNow && !estimate.pueden_enviarse) || (!sendNow && !scheduledAt)}
            className="w-full bg-[#F72585] hover:bg-[#E01B74] text-white h-11 text-sm font-semibold"
            data-testid="mass-launch-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {sendNow
              ? `Confirmar y enviar a ${estimate.pueden_enviarse} contactos`
              : 'Confirmar programación'}
          </Button>
        </div>
      )}

      {/* Nav */}
      {step < 3 && (
        <div className="flex justify-between">
          <Button variant="outline" size="sm" disabled={step === 0 || loading}
            onClick={() => { setError(''); setStep(s => s - 1); }}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
          </Button>
          <Button size="sm" disabled={!canNext() || loading}
            onClick={goNext}
            className="bg-[#0F172A] text-white"
            data-testid="mass-next">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {step === 2 ? 'Calcular a quién llega' : 'Siguiente'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
      {step === 3 && (
        <div className="flex justify-start">
          <Button variant="outline" size="sm" disabled={loading}
            onClick={() => { setError(''); setStep(2); setEstimate(null); }}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
          </Button>
        </div>
      )}
    </div>
  );
}
